<template>
  <div id="changePassword">
    <v-dialog
      v-model="dialogFlag"
      max-width="360px"
    >
      <v-card>
        <v-card-title>
          Change Password
        </v-card-title>

        <v-card-text>
          <v-form
            ref="passForm"
            v-model="validate"
          >
            <div>
              Old password
              <v-text-field
                v-model="password.old"
                :append-icon="show1 ? 'mdi-eye' : 'mdi-eye-off'"
                :rules="[...rules.rule1]"
                :type="show1 ? 'text' : 'password'"
                name="input-10-1"
                hint="At least 8 characters"
                counter
                dense
                @click:append="show1 = !show1"
              />
            </div>
            <div>
              New password
              <v-text-field
                v-model="password.new"
                :append-icon="show2 ? 'mdi-eye' : 'mdi-eye-off'"
                :rules="[...rules.rule2]"
                :type="show2 ? 'text' : 'password'"
                name="input-10-1"
                hint="At least 8 characters"
                counter
                dense
                @click:append="show2 = !show2"
              />
            </div>
            <div>
              Confirm new password
              <v-text-field
                v-model="password.confirm"
                :append-icon="show3 ? 'mdi-eye' : 'mdi-eye-off'"
                :rules="[...rules.rule3]"
                :type="show3 ? 'text' : 'password'"
                name="input-10-1"
                hint="At least 8 characters"
                counter
                dense
                @click:append="show3 = !show3"
                @keyup.enter="submitPassword"
              />
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions
          class="justify-center pb-5"
        >
          <v-btn
            outlined
            @click="dialogFlag = false"
          >
            Close
          </v-btn>
          <v-btn
            :loading="saveLoading"
            :disabled="saveLoading"
            color="primary"
            @click="confirmBeforeSubmit"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="changePassConfirm"
      max-width="400px"
    >
      <v-card>
        <v-card-title>
          Confirm Change
          <v-spacer />
          <v-btn
            icon
            @click="changePassConfirm = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text
          class="pb-3"
        >
          Changing your password will log you out of your current session,
          and redirect you to the login page.
        </v-card-text>
        <v-card-actions
          class="justify-center pb-3"
        >
          <v-btn
            rounded
            color="primary"
            @click="submitPassword"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import accountApi from '@/api/account/account';

export default {
  name: 'ChangePassword',

  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    saveLoading: false,
    validate: true,
    password: {
      old: '',
      new: '',
      confirm: '',
    },
    show1: false,
    show2: false,
    show3: false,
    changePassConfirm: false,
  }),

  computed: {
    rules() {
      // Init object
      const results = {};

      // name = rule name
      // key = data connection
      // rules = validator names in ruleHandler
      const rules = [
        {
          name: '1',
          key: 'old',
          rules: ['required', 'minimum'],
        },
        {
          name: '2',
          key: 'new',
          rules: ['required', 'minimum', 'regEx'],
        },
        {
          name: '3',
          key: 'confirm',
          rules: ['required', 'matched'],
        },
      ];

      // `results = {rule${name}: [true, 'False text', true...], ...}`
      rules.forEach((rule) => {
        const res = this.ruleHandler(rule);
        results[res.name] = res.result;
      });

      return results;
    },

    dialogFlag: {
      get() {
        return this.dialog;
      },
      set(newValue) {
        if (!newValue) {
          this.modalInit();
        }
        this.$emit('update:dialog', newValue);
      },
    },
  },

  methods: {
    ...mapActions('account', [
      'logout',
    ]),

    ruleHandler(rule) {
      // Get rule = { name, key, rules }
      // Validators
      const required = (key) => !!this.password[key] || `${key.charAt(0).toUpperCase() + key.substring(1)} password required.`;
      const minimum = (key) => (this.password[key]).length >= 8 || 'Password is at least 8 characters.';
      const matched = (key) => this.password.new === this.password[key] || 'Password does not match.';
      const regEx = (key) => {
        const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!";#$%&'()*+,./:<=>?@[^_`{|}\-\]~])[A-Za-z\d!";#$%&'()*+,./:<=>?@[^_`{|}\-\]~]{8,}$/;
        return regex.test(this.password[key]) || 'Password must include one upper character[A-Z], one integer[0-9], and one special character.';
      };

      // Return object init
      const result = {
        name: `rule${rule.name}`,
        result: [],
      };

      // Get rule results
      (rule.rules).forEach((item) => {
        switch (item) {
          case 'required':
            result.result.push(required(rule.key));
            break;
          case 'minimum':
            result.result.push(minimum(rule.key));
            break;
          case 'matched':
            result.result.push(matched(rule.key));
            break;
          case 'regEx':
            result.result.push(regEx(rule.key));
            break;
          default:
            break;
        }
      });

      return result;
    },

    confirmBeforeSubmit() {
      if (this.$refs.passForm.validate()) {
        this.changePassConfirm = true;
      }
    },

    async submitPassword() {
      try {
        this.changePassConfirm = false;
        this.saveLoading = true;
        await accountApi.updatePassword({
          old_password: this.password.old,
          new_password: this.password.new,
        });
        this.$toast.success('Your password has been reset');
        await this.logout();
        this.dialogFlag = false;
        await this.$router.push({ name: 'login' });
      } catch (err) {
        console.log(err);
        let message = err.msg || err.message;
        if (err.msg.includes('code 400')) {
          message = 'Check the old password.';
        }
        this.$toast.error(message);
      } finally {
        this.saveLoading = false;
      }
    },

    modalInit() {
      this.$refs.passForm.resetValidation();
      this.password.old = '';
      this.password.new = '';
      this.password.confirm = '';
    },
  },
};
</script>
